import { useCallback, useEffect, useState } from "react";
import type { AdminWallPlacement, AdminWallUpload } from "@gooner/shared";
import { WALL_DEFS, centsLabel, squareNumber, wallIdOfRegion } from "@gooner/shared";

/** "Community Wall · square 3" — a square named for a person, board first. */
const squareLabel = (regionId: string): string => {
  const wall = WALL_DEFS[wallIdOfRegion(regionId) ?? "main"];
  const n = squareNumber(regionId);
  return n ? `${wall.name} · square ${n}` : `${wall.name} · ${regionId}`;
};
import { adminApi } from "../lib/api";
import { Button } from "../control/ui/primitives";
import { FapsAmount } from "../control/ui/FapsAmount";

/**
 * The Community Wall's ledger — every placement ever, newest first, with the one
 * moderation lever the wall has: Revoke, the pull-after half of auto-approval.
 *
 * Revoking un-sells the square (it reverts to random catalog clips) and refunds
 * nothing; it does NOT resurrect whoever was displaced before this holder. Displaced
 * and revoked rows stay listed because the ledger is the receipt — the wall stores
 * cents, and a money table that forgets rows is not one.
 *
 * The clip ban hammer (Review → clip → ban) is the OTHER lever: a banned clip drops
 * out of every placement's rotation on the next materialization with no action here.
 */
export function WallAdmin() {
  const [placements, setPlacements] = useState<AdminWallPlacement[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { placements: list } = await adminApi.wallList();
      setPlacements(list);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function revoke(id: string) {
    setBusy(id);
    try {
      await adminApi.wallRevoke(id);
      // Reload rather than splice: revoking the live holder changes OTHER rows'
      // status too (there is a new "live" answer for the region — none).
      await load();
    } catch {
      void load();
    } finally {
      setBusy(null);
    }
  }

  const live = placements.filter((p) => p.status === "live").length;

  return (
    <div className="space-y-10">
      <UploadQueue />

      <div className="space-y-6">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-semibold text-zinc-100">Walls</h1>
          <p className="text-xs text-zinc-500">
            Every placement on both walls, newest first. Revoke un-sells the square — no
            refund, and the previously displaced holder does not come back.
          </p>
        </div>
        <span className="rounded-full bg-[var(--color-panel-2)] px-2.5 py-1 text-xs font-medium text-zinc-300">
          {live} live
        </span>
      </header>

      {loading ? (
        <p className="text-sm text-zinc-500">Loading…</p>
      ) : placements.length === 0 ? (
        <div className="rounded-xl border border-dashed border-[var(--color-edge)] p-8 text-center text-sm text-zinc-500">
          Nothing has been sold yet. Selling is a switch per wall in System → Community Wall / Premium Wall.
        </div>
      ) : (
        <ul className="space-y-2">
          {placements.map((p) => (
            <li
              key={p.id}
              className="flex items-center gap-3 rounded-xl border border-[var(--color-edge)] bg-[var(--color-panel)] p-2.5"
            >
              <StatusChip status={p.status} />
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium text-zinc-100">
                  @{p.username}
                  <span className="ml-2 font-normal text-zinc-500">
                    {p.tier} · {squareLabel(p.regionId)} · {p.clipCount}{" "}
                    {p.clipCount === 1 ? "clip" : "clips"}
                  </span>
                </div>
                <div className="truncate text-xs text-zinc-500">
                  {/* "free claim", never "paid $0.00" — the zero-dollar spelling is
                      reserved for the misconfigured-price alarm on the board. */}
                  {p.pricePaidCents === 0 ? (
                    "free claim"
                  ) : p.currency === "faps" ? (
                    <>
                      paid <FapsAmount n={p.pricePaidCents} size={10} gold={false} />
                    </>
                  ) : (
                    `paid ${centsLabel(p.pricePaidCents)}`
                  )}{" "}
                  ·{" "}
                  {new Date(p.createdAt).toLocaleString()}
                  {p.revokedAt
                    ? ` · revoked (${p.revokeReason}) ${new Date(p.revokedAt).toLocaleString()}`
                    : ""}
                </div>
              </div>
              {p.status !== "revoked" ? (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => void revoke(p.id)}
                  disabled={busy === p.id}
                >
                  {busy === p.id ? "Revoking…" : "Revoke"}
                </Button>
              ) : null}
            </li>
          ))}
        </ul>
      )}
      </div>
    </div>
  );
}

/**
 * What people have brought to the wall, pending first.
 *
 * Catalog picks never appear here — they were vetted before they were ever in the
 * catalog. This queue is only the media a buyer supplied themselves, which is the one
 * thing on this wall nobody has looked at yet, and a pending row is holding a square
 * somebody has already paid for. That is why the wait is the first thing each row says.
 */
function UploadQueue() {
  const [uploads, setUploads] = useState<AdminWallUpload[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { uploads: list } = await adminApi.wallUploadQueue();
      setUploads(list);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => {
    void load();
  }, [load]);

  async function act(u: AdminWallUpload, decision: "approve" | "reject") {
    if (
      decision === "reject" &&
      !window.confirm(
        `Reject this upload?\n\nThe square is revoked, the money is forfeited, and @${u.username} is banned. This cannot be undone from here.`,
      )
    ) {
      return;
    }
    setBusy(u.id);
    try {
      if (decision === "approve") await adminApi.wallUploadApprove(u.id);
      else await adminApi.wallUploadReject(u.id);
      // Reload rather than splice: rejecting changes a placement's status too, and the
      // ledger below is reading the same rows.
      await load();
    } catch {
      await load();
    } finally {
      setBusy(null);
    }
  }

  const pending = uploads.filter((u) => u.status === "pending");

  return (
    <div className="space-y-6">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-semibold text-zinc-100">Uploads awaiting review</h1>
          <p className="text-xs text-zinc-500">
            A buyer's own media. It plays nowhere until you approve it — their square is
            already up, on whatever else they picked. Rejecting revokes the square, keeps
            the money and bans the account.
          </p>
        </div>
        <span className="rounded-full bg-[var(--color-panel-2)] px-2.5 py-1 text-xs font-medium text-zinc-300">
          {pending.length} pending
        </span>
      </header>

      {loading ? (
        <p className="text-sm text-zinc-500">Loading…</p>
      ) : uploads.length === 0 ? (
        <div className="rounded-xl border border-dashed border-[var(--color-edge)] p-8 text-center text-sm text-zinc-500">
          Nobody has uploaded anything yet.
        </div>
      ) : (
        <ul className="space-y-2">
          {uploads.map((u) => (
            <li
              key={u.id}
              className="flex items-center gap-3 rounded-xl border border-[var(--color-edge)] bg-[var(--color-panel)] p-2.5"
            >
              <UploadThumb item={u.item} />
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium text-zinc-100">
                  @{u.username}
                  <span className="ml-2 font-normal text-zinc-500">
                    {u.item.kind}
                    {u.regionId ? ` · ${squareLabel(u.regionId)}` : u.feedPostId ? " · on the Feed" : " · no square yet"}
                  </span>
                </div>
                <div className="truncate text-xs text-zinc-500">
                  {u.status === "pending"
                    ? `waiting ${waited(u.createdAt)}`
                    : `${u.status}${u.reviewedReason ? ` — ${u.reviewedReason}` : ""}`}
                </div>
              </div>
              {u.status === "pending" ? (
                <div className="flex shrink-0 items-center gap-2">
                  <Button size="sm" onClick={() => void act(u, "approve")} disabled={busy === u.id}>
                    Approve
                  </Button>
                  <Button
                    size="sm"
                    variant="danger"
                    onClick={() => void act(u, "reject")}
                    disabled={busy === u.id}
                  >
                    Reject &amp; ban
                  </Button>
                </div>
              ) : (
                <StatusChip status={u.status === "live" ? "live" : "revoked"} />
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** How long somebody has been waiting on a square they already paid for. */
function waited(since: number): string {
  const mins = Math.max(0, Math.round((Date.now() - since) / 60_000));
  if (mins < 60) return `${mins}m`;
  const hours = Math.round(mins / 60);
  return hours < 48 ? `${hours}h` : `${Math.round(hours / 24)}d`;
}

/** The media itself — a moderator has to SEE it, so the video plays muted on hover the
 *  way the tag queue's thumb does. */
function UploadThumb({ item }: { item: AdminWallUpload["item"] }) {
  return item.kind === "image" ? (
    <img src={item.url} alt="" className="h-14 w-20 shrink-0 rounded object-cover" />
  ) : (
    <video
      src={item.url}
      className="h-14 w-20 shrink-0 rounded object-cover"
      muted
      loop
      playsInline
      preload="metadata"
      onMouseEnter={(e) => void e.currentTarget.play().catch(() => undefined)}
      onMouseLeave={(e) => e.currentTarget.pause()}
    />
  );
}

function StatusChip({ status }: { status: AdminWallPlacement["status"] }) {
  const styles: Record<AdminWallPlacement["status"], string> = {
    live: "bg-emerald-500/15 text-emerald-300",
    displaced: "bg-zinc-500/15 text-zinc-400",
    revoked: "bg-red-500/15 text-red-300",
  };
  return (
    <span
      className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium uppercase tracking-wide ${styles[status]}`}
    >
      {status}
    </span>
  );
}
